/* @jsx h */
import { defineComponent, h, ref, onMounted, onUnmounted } from "vue";
import { RouterLink } from "vue-router";
import { useBranding } from "../providers/Branding";
import lemmonLogo from "@app/images/lemmon-logo-full.svg?raw";
import { useLocalization } from "@lemmonhq/uikit";

interface Props {
  title?: string;
  subtitle?: string;
  showBackButton?: boolean;
  backTo?: string;
  backText?: string;
}

export const PublicHeaderComponent = defineComponent<Props>({
  props: ["title", "subtitle", "showBackButton", "backTo", "backText"],
  setup(props, { slots }) {
    const { branding } = useBranding();
    const { t } = useLocalization();
    const scrolled = ref(false);
    let ticking = false;

    onMounted(() => {
      const onScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const shouldScroll = window.scrollY > 50;
            if (scrolled.value !== shouldScroll) {
              scrolled.value = shouldScroll;
            }
            ticking = false;
          });
          ticking = true;
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      onUnmounted(() => window.removeEventListener("scroll", onScroll));
    });

    const LemmonLogo = () => (
      <div
        style="height: 38px;"
        class="logo-container relative my-4"
        v-html={lemmonLogo}
      />
    );

    const JavshaniLogo = () => (
      <div
        class="h-12 w-12 sm:h-16 sm:w-16 shrink-0 overflow-hidden transition-transform group-hover:scale-105"
        style="mix-blend-mode: multiply;"
      >
        <img
          src="/javshani.jpg"
          alt="JAVSHANI"
          class="w-full h-auto"
          style="object-fit: cover; object-position: top; max-height: 140%; margin-top: -2px;"
        />
      </div>
    );

    return () => (
      <header
        class={` top-0  border-b ${
          scrolled.value ? "border-gray-200" : "border-gray-100"
        }`}
        style={{
          backgroundColor: scrolled.value ? "rgba(255,255,255,0.92)" : "#fff",
          backdropFilter: scrolled.value ? "blur(8px) saturate(180%)" : "none",
          WebkitBackdropFilter: scrolled.value
            ? "blur(8px) saturate(180%)"
            : "none",
          boxShadow: scrolled.value ? "0 1px 8px rgba(0,0,0,0.06)" : "none",
          transition:
            "background-color 300ms ease, box-shadow 300ms ease, border-color 200ms ease, backdrop-filter 300ms ease",
        }}
      >
        <div class="max-w-7xl mx-auto px-4 py-0">
          {/* Wraps to two rows only when the controls cannot fit beside the
              brand (narrow covers such as the Galaxy Fold), so the header never
              becomes wider than the viewport. */}
          <div class="flex flex-wrap items-center justify-between gap-y-1">
            <RouterLink to="/" class="">
              {branding.value?.id === "javshani" ? (
                <div class="py-2 flex flex items-center gap-1 sm:gap-2 group shrink-0">
                  <JavshaniLogo />
                  <div class="text-lg sm:text-2xl uppercase font-bold tracking-wide text-gray-900 whitespace-nowrap">
                    {branding.value?.name}
                  </div>
                </div>
              ) : (
                <LemmonLogo />
              )}
            </RouterLink>

            <div class="flex items-center gap-3 sm:gap-6 ml-auto">
              {branding.value?.id === "javshani" && (
                // Plain anchor: /about is served by the worker, not the SPA router
                <a
                  href="/about"
                  class="text-gray-600 hover:text-gray-900 font-light text-sm sm:text-base whitespace-nowrap"
                  style="transition: color 200ms ease;"
                >
                  {t("customer.home.about_us")}
                </a>
              )}

              {slots.default?.()}

              {props.showBackButton && (
                <RouterLink
                  to={props.backTo || "/"}
                  class="text-gray-600 hover:text-gray-900 font-light"
                  style="transition: color 200ms ease;"
                >
                  {props.backText ||
                    `← ${t("customer.home.back_to_directory")}`}
                </RouterLink>
              )}
            </div>
          </div>
        </div>
      </header>
    );
  },
});
